/**
 * THREE.js Renderer management
 */

export default class Renderer {
    constructor(container, config) {
        this.container = container;
        this.config = config;
        this.renderer = null;
        this.boundOnResize = this.onResize.bind(this);
        
        this.init();
    }

    init() {
        // Create WebGL renderer
        this.renderer = new THREE.WebGLRenderer({ 
            antialias: true,
            alpha: true
        });
        
        // Set size
        const width = this.container.clientWidth;
        const height = this.container.clientHeight;
        this.renderer.setSize(width, height);
        this.renderer.setPixelRatio(window.devicePixelRatio);
        
        // Set background color
        const bgColor = this.config.get('viewportBackground');
        this.renderer.setClearColor(this.hexToNumber(bgColor));
        
        // Enable shadows
        this.renderer.shadowMap.enabled = true;
        this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
        
        // Append to container
        this.container.appendChild(this.renderer.domElement);
        
        // Add resize listener
        window.addEventListener('resize', this.boundOnResize);
        
        // Observe the container itself so embedded/hidden layouts resize
        // without a window resize (F20)
        if (typeof ResizeObserver !== 'undefined') {
            this.resizeObserver = new ResizeObserver(() => this.onResize());
            this.resizeObserver.observe(this.container);
        }
    }

    /**
     * Handle window or container resize
     */
    onResize() {
        const width = this.container.clientWidth;
        const height = this.container.clientHeight;
        
        // Guard zero dimensions (e.g. hidden containers) (F20)
        if (width <= 0 || height <= 0) return;
        
        this.renderer.setSize(width, height);
    }

    /**
     * Convert hex string to number
     */
    hexToNumber(hex) {
        return parseInt(hex.replace('#', ''), 16);
    }

    /**
     * Render scene with camera
     */
    render(scene, camera) {
        this.renderer.render(scene, camera);
    }

    /**
     * Get renderer instance
     */
    getRenderer() {
        return this.renderer;
    }

    /**
     * Set background color
     */
    setBackgroundColor(color) {
        this.renderer.setClearColor(this.hexToNumber(color));
    }

    /**
     * Get canvas element
     */
    getCanvas() {
        return this.renderer.domElement;
    }

    /**
     * Dispose renderer
     */
    dispose() {
        window.removeEventListener('resize', this.boundOnResize);
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }
        if (this.renderer) {
            this.renderer.dispose();
            if (this.renderer.domElement.parentNode) {
                this.renderer.domElement.parentNode.removeChild(this.renderer.domElement);
            }
        }
    }
}
